import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui'
import { useTranslation } from '@/contexts/TranslationContext'
import { getUnit, updateUnit } from '@/lib/services/units'

interface DefaultMarginUpdateModalProps {
  unitId: string
  isOpen: boolean
  onClose: () => void
  onUpdated?: () => void
}

export function DefaultMarginUpdateModal({ unitId, isOpen, onClose, onUpdated }: DefaultMarginUpdateModalProps) {
  const { t } = useTranslation()
  const [marginPercentage, setMarginPercentage] = useState<number>(30)
  const [currentAttrs, setCurrentAttrs] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [inputError, setInputError] = useState<string>('')

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (isOpen && unitId) {
      loadCurrentMargin()
    }
  }, [isOpen, unitId])

  const loadCurrentMargin = async () => {
    setIsLoading(true)
    try {
      const unit = await getUnit(unitId)
      const attrs = (unit as any).attrs ?? {}
      setCurrentAttrs(attrs)
      setMarginPercentage(attrs.margin_percentage ?? 30)
      setInputError('')
    } catch (error) {
      console.error('[DefaultMarginUpdateModal] Error loading unit:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (marginPercentage < 0 || marginPercentage > 100) {
      setInputError('A margem deve estar entre 0% e 100%')
      return
    }

    setIsSaving(true)
    try {
      await updateUnit(unitId, {
        attrs: { ...currentAttrs, margin_percentage: marginPercentage }
      })
      onUpdated?.()
      onClose()
    } catch (error) {
      console.error('[DefaultMarginUpdateModal] Error saving margin:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleInputChange = (value: string) => {
    const numValue = parseFloat(value) || 0
    setMarginPercentage(numValue)
    setInputError('')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('units.record.updateDefaultMarginTitle')}
      fullScreen={isMobile}
      mobileTopAligned={isMobile}
      headerLeft={
        isMobile ? (
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
            aria-label={t('actions.cancel')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : undefined
      }
      headerRight={
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading || !!inputError}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label={t('actions.save')}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
          {!isMobile && (
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
              aria-label={t('actions.close')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Defina a margem de lucro padrão (em porcentagem) para esta área de trabalho. Este valor será usado como padrão em cálculos de precificação.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-r-transparent" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                  Margem de Lucro (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={marginPercentage}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 transition-colors ${
                    inputError
                      ? 'border-red-300 dark:border-red-600 focus:ring-red-500'
                      : 'border-slate-300 dark:border-slate-600 focus:ring-emerald-500'
                  }`}
                  disabled={isSaving}
                />
              </div>
              <div className="text-sm font-semibold text-slate-900 dark:text-white">
                {marginPercentage}%
              </div>
            </div>

            {inputError && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400">{inputError}</p>
              </div>
            )}

            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                <span className="font-semibold">Exemplos:</span>
              </p>
              <ul className="text-sm text-slate-600 dark:text-slate-400 mt-2 space-y-1">
                <li>• 30% - Margem conservadora</li>
                <li>• 50% - Margem média</li>
                <li>• 100% - Lucro 100% sobre custo</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
